import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import IcpFormPreview from '../components/IcpFormPreview';
import PackagePicker, { PACKAGES } from '../components/PackagePicker';
import PaymentModal from '../components/PaymentModal';
import './OrderPage.css';

// The signed offer token is `base64url(JSON).base64url(sig)`. The payload is not
// encrypted (only signed), so we can read the full list of offered domains from
// it to let the buyer switch between them. The backend re-validates the chosen
// domain + its price against the same token, so this is display-only and safe.
function decodeOfferTokenOffers(token) {
  if (!token) return [];
  try {
    const seg = String(token).split('.')[0];
    const b64 = seg.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? '='.repeat(4 - (b64.length % 4)) : '';
    const payload = JSON.parse(atob(b64 + pad));
    if (!Array.isArray(payload.offers)) return [];
    return payload.offers
      .filter((o) => o && o.domain)
      .map((o) => ({ domain: String(o.domain), price: Number(o.price) || null }));
  } catch (_e) {
    return [];
  }
}

function OrderPage() {
  const [searchParams] = useSearchParams();
  const [orderData, setOrderData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState('domain_only');
  const [selectedDomain, setSelectedDomain] = useState(null);

  useEffect(() => {
    // Extract order data from URL params
    const domain = searchParams.get('domain');
    const leadId = searchParams.get('lead_id');
    const packageType = searchParams.get('package_type') || 'domain_only';
    const mockupPath = searchParams.get('mockup');
    const offerToken = searchParams.get('offer_token');
    const email = searchParams.get('email');
    const company = searchParams.get('company');
    const industry = searchParams.get('industry');
    const location = searchParams.get('location');
    const customerPrice = parseFloat(searchParams.get('customer_price')) || null;

    setSelectedPackage(packageType);

    if (domain) {
      setSelectedDomain(domain);
      setOrderData({
        domain,
        leadId,
        packageType,
        mockupPath,
        offerToken,
        email,
        company,
        industry,
        location,
        customerPrice,
      });
    }
    setLoading(false);
  }, [searchParams]);

  // All domains offered in the single email link, decoded from the signed token.
  const offeredDomains = useMemo(() => {
    const offers = decodeOfferTokenOffers(orderData?.offerToken);
    // Ensure the URL domain is always selectable even if the token is absent/stale.
    if (orderData?.domain && !offers.some((o) => o.domain === orderData.domain)) {
      offers.unshift({ domain: orderData.domain, price: orderData.customerPrice });
    }
    return offers;
  }, [orderData]);

  if (loading) {
    return (
      <div className="order-page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  if (!orderData || !orderData.domain) {
    return (
      <div className="order-page">
        <div className="error">
          <h1>Invalid Order</h1>
          <p>Missing domain information. Please use the link from your email.</p>
        </div>
      </div>
    );
  }

  // Active domain = whichever option the buyer picked from the single link.
  const activeDomain = selectedDomain || orderData.domain;
  const activeOffer = offeredDomains.find((o) => o.domain === activeDomain);

  // Dynamic pricing: price follows the selected domain (token is authoritative server-side).
  const domainPrice = activeOffer?.price || orderData?.customerPrice || PACKAGES[0].price;
  const landingAddon = 60;
  const videoAddon = 51;
  const currentPrice = selectedPackage === 'domain_and_landing_page_and_video'
    ? domainPrice + landingAddon + videoAddon
    : selectedPackage === 'domain_and_landing_page'
      ? domainPrice + landingAddon
      : domainPrice;
  const selectedPackageMeta = PACKAGES.find((pkg) => pkg.id === selectedPackage) || PACKAGES[0];

  // Build orderData with current selection for PaymentModal
  const activeOrderData = orderData ? {
    ...orderData,
    domain: activeDomain,
    customerPrice: domainPrice,
    price: currentPrice,
    packageType: selectedPackage,
  } : null;

  return (
    <div className="order-page">
      <main className="order-main">
        <div className="container">
          <div className="order-grid">
          <div className="order-card-wrapper">
            <div className="order-card">
              {/* Domain Header Section */}
              <div className="domain-hero">
                <div className="availability-badge"><i className="fa-solid fa-circle-check" aria-hidden="true" /> Available now</div>
                <h1 className="domain-name">{activeDomain}</h1>
                <p className="domain-tagline">
                  Reserve this domain for your ICP campaign. We’ll register and provision it right after payment.
                </p>
                <div className="package-inline-summary">
                  <span className="package-inline-pill">
                    <i className="fa-solid fa-box-open" aria-hidden="true" /> {selectedPackageMeta.name}
                  </span>
                  <span className="package-inline-price">${currentPrice.toFixed(2)} total</span>
                </div>
              </div>

              {/* Domain switcher — all options from the single email link */}
              {offeredDomains.length > 1 && (
                <div className="domain-switcher">
                  <div className="domain-switcher__label">
                    Choose your domain <span className="domain-switcher__count">{offeredDomains.length} options</span>
                  </div>
                  <div className="domain-switcher__options">
                    {offeredDomains.map((o) => (
                      <button
                        key={o.domain}
                        type="button"
                        className={`domain-chip${o.domain === activeDomain ? ' is-active' : ''}`}
                        onClick={() => setSelectedDomain(o.domain)}
                        aria-pressed={o.domain === activeDomain}
                      >
                        <span className="domain-chip__name">{o.domain}</span>
                        {o.price ? <span className="domain-chip__price">${o.price.toFixed(0)}</span> : null}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <PackagePicker selectedPackage={selectedPackage} onSelectPackage={setSelectedPackage} customerPrice={domainPrice} />

              {/* Mockup Preview (if available) */}
              {orderData.mockupPath && (
                <div className="mockup-preview">
                  <img
                    src={orderData.mockupPath}
                    alt={`${orderData.domain} website preview`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}

              {/* Primary CTA */}
              <button type="button" className="btn btn-primary btn-large" onClick={() => setIsPaymentOpen(true)}>
                Reserve {selectedPackageMeta.name} for ${currentPrice.toFixed(2)}
              </button>

              {/* Trust Badges */}
              <div className="trust-badges">
                <div className="trust-badge"><i className="fa-solid fa-screwdriver-wrench" aria-hidden="true" /> Provisioning starts after payment</div>
                <div className="trust-badge"><i className="fa-solid fa-shield-halved" aria-hidden="true" /> Secure checkout</div>
                <div className="trust-badge"><i className="fa-solid fa-rotate-left" aria-hidden="true" /> 7-day money-back promise</div>
              </div>

              {/* Availability Disclaimer */}
              <p className="availability-note">
                Domain availability can change. If the domain is no longer available at checkout, we'll immediately suggest alternatives or process your refund.
              </p>
            </div>
          </div>

          <div className="order-preview-col" aria-hidden="true">
            {(selectedPackage === 'domain_and_landing_page' || selectedPackage === 'domain_and_landing_page_and_video') ? (
              <>
                <div className="order-preview-caption">
                  {selectedPackage === 'domain_and_landing_page_and_video'
                    ? 'Your landing page + promo video'
                    : 'Live preview of your landing page'}
                </div>
                <div className="order-preview-stage">
                  <IcpFormPreview
                    domain={activeDomain}
                    company={orderData.company}
                    industry={orderData.industry}
                    location={orderData.location}
                    visible
                    mode="clear"
                    placement="band"
                  />
                </div>
                {selectedPackage === 'domain_and_landing_page_and_video' && (
                  <div className="order-preview-video">
                    <div className="opv-frame">
                      <span className="opv-play"><i className="fa-solid fa-play" aria-hidden="true" /></span>
                      <span className="opv-meta">
                        <span className="opv-title">Custom promo video included</span>
                        <span className="opv-sub">20–30s · vertical 9:16 · delivered in 24h</span>
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="order-preview-domain-placeholder">
                <div className="opdp-inner">
                  <div className="opdp-eyebrow">Your domain</div>
                  <div className="opdp-name">
                    <span className="opdp-name__base">{activeDomain.includes('.') ? activeDomain.split('.').slice(0, -1).join('.') : activeDomain}</span>
                    <span className="opdp-name__ext">{activeDomain.includes('.') ? '.' + activeDomain.split('.').slice(-1)[0] : ''}</span>
                  </div>
                  <ul className="opdp-features">
                    <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> Domain registration</li>
                    <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> DNS provisioning</li>
                    <li><i className="fa-solid fa-circle-check" aria-hidden="true" /> Full ownership transfer</li>
                  </ul>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
      </main>

      {isPaymentOpen && (
        <PaymentModal
          orderData={activeOrderData}
          onClose={() => setIsPaymentOpen(false)}
          onSuccess={(orderId) => {
            setIsPaymentOpen(false);
            window.location.href = `/order/success?order_id=${orderId}&email=${encodeURIComponent(activeOrderData?.email || '')}`;
          }}
        />
      )}
    </div>
  );
}

export default OrderPage;
